import { createContext, useContext, useEffect, useState } from "react";
import { api, formatApiError, markLoggedInNow, USE_COOKIE_AUTH, TOKEN_KEY } from "@/lib/api";

const AuthContext = createContext(null);

function _slugFromUrl() {
  const p = new URLSearchParams(window.location.search);
  const q = (p.get("tenant") || "").trim().toLowerCase();
  if (q) return q;
  const m = window.location.pathname.match(/^\/t\/([a-z0-9_-]+)(\/|$)/i);
  return m ? m[1].toLowerCase() : "";
}

export function AuthProvider({ children }) {
  const [user, setUser] = useState(null);
  const [error, setError] = useState("");

  async function fetchMe() {
    try {
      const { data } = await api.get("/auth/me");
      setUser(data);
    } catch (e) {
      setUser(false);
    }
  }

  useEffect(() => { fetchMe(); }, []);

  async function login(email, password) {
    setError("");
    try {
      const slug = _slugFromUrl();
      const url = slug ? `/auth/login?tenant=${encodeURIComponent(slug)}` : "/auth/login";
      const { data } = await api.post(url, { email, password });
      // Phase G-Security Auth-Transport (see lib/api.js → USE_COOKIE_AUTH).
      // In COOKIE mode the backend sets HttpOnly+Secure cookies in the
      // response, and we DELIBERATELY don't touch localStorage — the cookie
      // alone authenticates every subsequent request.
      // In BEARER mode (current default until the ingress CORS is fixed)
      // we persist the JWT in localStorage so the request interceptor can
      // attach it as `Authorization: Bearer`.
      if (!USE_COOKIE_AUTH && data?.token) {
        try { localStorage.setItem(TOKEN_KEY, data.token); } catch (_) {}
      }
      try {
        const { data: full } = await api.get("/auth/me");
        setUser(full);
      } catch {
        setUser(data.user);
      }
      return true;
    } catch (e) {
      const msg = formatApiError(e.response?.data?.detail) || e.message;
      setError(msg);
      return false;
    }
  }

  async function logout() {
    try { await api.post("/auth/logout"); } catch (_) {}
    // Even in cookie mode we clear localStorage defensively — covers users
    // mid-migration who still have a stale `mt_token` lying around.
    try { localStorage.removeItem(TOKEN_KEY); } catch (_) {}
    setUser(false);
  }

  async function changePassword(oldPw, newPw) {
    const { data } = await api.post("/auth/change-password", {
      old_password: oldPw,
      new_password: newPw,
    });
    if (data?.ok) setUser((u) => (u ? { ...u, must_change_password: false } : u));
    return data;
  }

  return (
    <AuthContext.Provider value={{ user, login, logout, error, setError, changePassword, refreshMe: fetchMe }}>
      {children}
    </AuthContext.Provider>
  );
}

export const useAuth = () => useContext(AuthContext);
